import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_ENDPOINTS } from '../constants/apiConstants';
import { getStoredTokens, getStoredUserId, getUserInfo } from '../utils/auth';
import './CSS/Gallery.css';

const getLinkedInCaptionSuggestion = (suggestions) => {
  const linkedInPlatform = suggestions?.platforms?.find((platform) => {
    const platformName = platform?.platform_name || '';
    return platformName.toLowerCase() === 'linkedin';
  });

  return linkedInPlatform?.professional_caption
    || linkedInPlatform?.caption
    || suggestions?.catchy_pharse
    || '';
};

const Gallery = () => {
  const [prompt, setPrompt] = useState('');
  const [products, setProducts] = useState('');
  const [marketingGoal, setMarketingGoal] = useState('Product launch promo');
  const [count, setCount] = useState(3);
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [error, setError] = useState('');
  const [deletingItemId, setDeletingItemId] = useState(null);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [modalDesc, setModalDesc] = useState('');
  const [modalPlatform, setModalPlatform] = useState('linkedin');
  const [platformOrgs, setPlatformOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsError, setOrgsError] = useState('');
  const [selectedOrgs, setSelectedOrgs] = useState({});
  const [selectAllOrgs, setSelectAllOrgs] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // Multi-select gallery state
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedGalleryItems, setSelectedGalleryItems] = useState({});

  // Separate scheduling: step through selected items one by one
  const [separateScheduleQueue, setSeparateScheduleQueue] = useState([]); // items left to schedule
  const [separateScheduleIndex, setSeparateScheduleIndex] = useState(0);  // current position
  const [isSeparateMode, setIsSeparateMode] = useState(false);            // vs group mode
  const [separateScheduleResults, setSeparateScheduleResults] = useState([]); // completed items

  // Scheduler state
  const [postMode, setPostMode] = useState('now'); // 'now' | 'schedule' | 'ai'
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);

  const userInfo = getUserInfo();

  const buildBffUrl = (path) => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const baseUrl = API_BASE_URL?.replace(/\/$/, '') || '';
    if (baseUrl.endsWith('/api/bff') && normalizedPath.startsWith('/api/bff/')) {
      return `${baseUrl}${normalizedPath.replace('/api/bff', '')}`;
    }
    return `${baseUrl}${normalizedPath}`;
  };

  const normalizeOrg = (org) => {
    const organization_id = org.organization_id || org.organizationId || org.id || org.page_id || org.pageId || org.instagram_account_id;
    const organization_name = org.organization_name || org.name || org.localizedName || org.page_name || org.pageName || org.username || org.displayName || org.title || organization_id;
    return {
      ...org,
      organization_id,
      organization_name
    };
  };

  const getRequestUserId = () => {
    const storedUserId = getStoredUserId();
    if (storedUserId) return storedUserId;

    const tokens = getStoredTokens();
    if (tokens?.idToken) {
      try {
        const parts = tokens.idToken.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          if (payload?.sub) return payload.sub;
        }
      } catch (err) {
        console.warn('Unable to derive user ID from token', err);
      }
    }

    return null;
  };

  const tokens = getStoredTokens();

  const fetchGallery = async () => {
    try {
      if (!tokens?.idToken) return;
      const userid = getRequestUserId();
      const response = await fetch(buildBffUrl(API_ENDPOINTS.GALLERY_LIST), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokens.idToken}`,
          'userid': userid || ''
        }
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.items)) {
        setGalleryItems(data.items);
      }
    } catch (err) {
      console.error('Failed to load gallery', err);
    }
  };

  useEffect(() => {
    fetchGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setReferenceFiles(files.slice(0, 5));
  };

  const fetchSocialSuggestions = async (item) => {
    const posterUrl = item?.s3_url || item?.url || item?.publicUrl || '';
    if (!posterUrl) {
      setSuggestionsError('No image URL is available for caption suggestions.');
      return;
    }

    try {
      setSuggestionsLoading(true);
      setSuggestionsError('');

      const formData = new FormData();
      formData.append('poster_url', posterUrl);
      if (item?.prompt) {
        formData.append('name', item.prompt);
      }

      const response = await fetch(buildBffUrl('/images/social-suggestions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'userid': getRequestUserId() || ''
        },
        body: JSON.stringify({
          poster_url: posterUrl,
          name: item?.prompt || undefined
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || data.error || 'Failed to fetch AI suggestions');
      }

      setAiSuggestions(data);
      const suggestedCaption = getLinkedInCaptionSuggestion(data);
      if (suggestedCaption) {
        setModalDesc(suggestedCaption);
      }
    } catch (err) {
      console.error('Failed to fetch social suggestions', err);
      setSuggestionsError(err.message || 'Failed to load AI suggestions.');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const openUploadModal = (item) => {
    setModalImage(item);
    setModalDesc(item.prompt || '');
    setModalPlatform('linkedin');
    setPlatformOrgs([]);
    setOrgsError('');
    setSelectedOrgs({});
    setSelectAllOrgs(false);
    setAiSuggestions(null);
    setSuggestionsError('');
    setStatusMessage('');
    setPostMode('now');
    setScheduleDate('');
    setScheduleTime('');
    setShowUploadModal(true);
    fetchOrganizations('linkedin');
    fetchSocialSuggestions(item);
  };

  // ── Multi-select helpers ──────────────────────────────────────────────────
  const toggleGalleryItemSelect = (itemKey) => {
    setSelectedGalleryItems(prev => ({ ...prev, [itemKey]: !prev[itemKey] }));
  };

  const getSelectedItems = () =>
    galleryItems.filter(item => selectedGalleryItems[item.s3_key || item.s3_url]);

  // ── Open modal helpers ────────────────────────────────────────────────────

  const _openModalForItem = (item, mode = 'now', isSep = false) => {
    setModalImage(item);
    setModalDesc(item.prompt || '');
    setModalPlatform('linkedin');
    setPlatformOrgs([]);
    setOrgsError('');
    setSelectedOrgs({});
    setSelectAllOrgs(false);
    setAiSuggestions(null);
    setSuggestionsError('');
    setStatusMessage('');
    setPostMode(mode);
    setScheduleDate('');
    setScheduleTime('');
    setIsSeparateMode(isSep);
    setShowUploadModal(true);
    fetchOrganizations('linkedin');
    fetchSocialSuggestions(item);
  };

  /**
   * "Schedule as Group" — one modal, all selected images bundled into a single
   * scheduled post via image_urls[].
   */
  const openGroupScheduleModal = () => {
    const items = getSelectedItems();
    if (items.length === 0) return;
    _openModalForItem(items[0], 'schedule', false);
  };

  /**
   * "Schedule Separately" — step through each selected image one at a time.
   * Each image gets its own modal and its own scheduled_posts row.
   */
  const openSeparateScheduleModal = () => {
    const items = getSelectedItems();
    if (items.length === 0) return;
    setSeparateScheduleQueue(items);
    setSeparateScheduleIndex(0);
    setSeparateScheduleResults([]);
    _openModalForItem(items[0], 'schedule', true);
  };

  /** Advance to the next item in the separate-schedule queue. */
  const advanceToNextSeparateItem = (result = null) => {
    const nextResults = result
      ? [...separateScheduleResults, result]
      : separateScheduleResults;
    setSeparateScheduleResults(nextResults);

    const nextIndex = separateScheduleIndex + 1;
    if (nextIndex < separateScheduleQueue.length) {
      setSeparateScheduleIndex(nextIndex);
      const nextItem = separateScheduleQueue[nextIndex];
      // Reset modal state for next item
      setModalImage(nextItem);
      setModalDesc(nextItem.prompt || '');
      setSelectedOrgs({});
      setSelectAllOrgs(false);
      setAiSuggestions(null);
      setSuggestionsError('');
      setStatusMessage('');
      setScheduleDate('');
      setScheduleTime('');
      setIsScheduling(false);
      fetchSocialSuggestions(nextItem);
    } else {
      // All done
      const allResults = nextResults;
      const successCount = allResults.filter(r => r.success).length;
      closeUploadModal();
      clearMultiSelect();
      setStatusMessage(
        `Scheduled ${successCount} of ${allResults.length} post${allResults.length > 1 ? 's' : ''} successfully.`
      );
    }
  };

  const clearMultiSelect = () => {
    setSelectedGalleryItems({});
    setMultiSelectMode(false);
    setSeparateScheduleQueue([]);
    setSeparateScheduleIndex(0);
    setSeparateScheduleResults([]);
    setIsSeparateMode(false);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setModalImage(null);
    setAiSuggestions(null);
    setSuggestionsLoading(false);
    setSuggestionsError('');
    setIsPosting(false);
    setIsScheduling(false);
    setPostMode('now');
    setScheduleDate('');
    setScheduleTime('');
    setIsSeparateMode(false);
    setSeparateScheduleQueue([]);
    setSeparateScheduleIndex(0);
    setSeparateScheduleResults([]);
  };

  const syncLinkedInPosts = async (orgId) => {
    if (!orgId) return;
    try {
      const userid = getRequestUserId();
      if (!userid) return;
      const tokens = getStoredTokens();

      await fetch(buildBffUrl(`/linkedin/posts/${orgId}/sync`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens?.idToken}`,
          'userid': userid,
          'Content-Type': 'application/json'
        }
      });
      console.log('LinkedIn posts synced after posting');
    } catch (err) {
      console.error('Failed to sync LinkedIn posts:', err);
      // Sync failure should not block the user flow
    }
  };

  const fetchOrganizations = async (platform) => {
    const userid = getRequestUserId();
    if (!userid) return;
    try {
      setOrgsLoading(true);
      setOrgsError('');
      // Map platform to BFF endpoints
      // LinkedIn has a dedicated endpoint
      if (platform === 'linkedin') {
        const res = await fetch(buildBffUrl('/linkedin/organizations'), {
          method: 'GET',
          headers: { 'userid': userid }
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.organizations)) {
          const orgs = data.organizations.map(normalizeOrg).filter(o => o.organization_id);
          setPlatformOrgs(orgs);
          const init = {};
          orgs.forEach((o) => { init[o.organization_id] = false; });
          setSelectedOrgs(init);
        } else {
          setPlatformOrgs([]);
          setOrgsError(`No ${platform} organizations found.`);
        }
        return;
      }

      // For Facebook/Instagram fetch from /meta/data and extract pages/accounts
      if (platform === 'facebook' || platform === 'instagram') {
        const res = await fetch(buildBffUrl('/meta/data'), {
          method: 'GET',
          headers: { 'userid': userid }
        });
        const data = await res.json();
        if (data.success && data.data) {
          // Facebook pages are in data.data.accounts or facebook_pages
          const pages = data.data.accounts || data.data.facebook_pages || [];
          // Normalize to organization-like objects
          const orgs = pages.map(p => normalizeOrg({
            ...p,
            organization_id: p.page_id || p.id || p.pageId || p.facebook_page_id || p.facebookPageId,
            organization_name: p.page_name || p.name || p.pageName || p.username || p.displayName
          })).filter(o => o.organization_id);
          setPlatformOrgs(orgs);
          const init = {};
          orgs.forEach((o) => { init[o.organization_id] = false; });
          setSelectedOrgs(init);
        } else {
          setPlatformOrgs([]);
          setOrgsError(`No ${platform} organizations found.`);
        }
        return;
      }

      // Fallback: try platform-specific path
      const res = await fetch(buildBffUrl(`/${platform}/organizations`), {
        method: 'GET',
        headers: { 'userid': userid }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.organizations)) {
        const orgs = data.organizations.map(normalizeOrg).filter(o => o.organization_id);
        setPlatformOrgs(orgs);
        const init = {};
        orgs.forEach((o) => { init[o.organization_id] = false; });
        setSelectedOrgs(init);
      } else {
        setPlatformOrgs([]);
        setOrgsError(`No ${platform} organizations found.`);
      }
    } catch (err) {
      console.error('Failed to fetch organizations', err);
      setPlatformOrgs([]);
      setOrgsError('Failed to load organizations.');
    } finally {
      setOrgsLoading(false);
    }
  };

  const togglePlatform = async (platform) => {
    setModalPlatform(platform);
    await fetchOrganizations(platform);
  };

  const toggleOrgCheckbox = (orgId) => {
    setSelectedOrgs(prev => ({ ...prev, [orgId]: !prev[orgId] }));
  };

  const toggleSelectAll = () => {
    const newVal = !selectAllOrgs;
    setSelectAllOrgs(newVal);
    const updated = {};
    platformOrgs.forEach(o => {
      const id = o.organization_id || o.id || o.id_str || o.idString;
      updated[id] = newVal;
    });
    setSelectedOrgs(updated);
  };

  const handlePostToPlatforms = async () => {
    if (!tokens?.idToken) { setError('Please log in'); return; }
    const userid = getRequestUserId();
    if (!userid) { setError('User ID not found. Please log in again.'); return; }
    const orgIds = Object.keys(selectedOrgs).filter(k => selectedOrgs[k]);
    if (orgIds.length === 0) { setError('Select at least one organization'); return; }
    const recipientEmail = userInfo?.email || '';
    const imageUrl = modalImage?.s3_url || modalImage?.s3_key || modalImage?.url || modalImage?.publicUrl;
    const platformLabel = modalPlatform.charAt(0).toUpperCase() + modalPlatform.slice(1);

    setIsPosting(true);
    setError('');
    try {
      const payload = {
        caption: modalDesc,
        selected_caption: modalDesc,
        image_url: imageUrl,
        organization_id: orgIds[0],
        organization_ids: orgIds,
        platform: modalPlatform,
        recipientEmail,
        ai_response: aiSuggestions || {},
      };

      // Map platform to post endpoint
      const postEndpoint = modalPlatform === 'linkedin' ? '/linkedin/post/ai' : `/${modalPlatform}/post`;
      const res = await fetch(buildBffUrl(postEndpoint), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.idToken}`,
          'Content-Type': 'application/json',
          'userid': userid
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Post failed');

      const postUrl = data.notification?.post_url || data.data?.share?.url || data.data?.posting_result?.data?.share?.url || null;
      const emailSent = data.notification?.email_sent;
      setStatusMessage(
        `${platformLabel} post published${postUrl ? `: ${postUrl}` : ''}${emailSent ? ' and emailed to the user.' : ' (email not sent).'}`
      );

      // Sync LinkedIn posts after successful post
      if (modalPlatform === 'linkedin') {
        const orgIds = Object.keys(selectedOrgs).filter(k => selectedOrgs[k]);
        if (orgIds.length > 0) {
          await syncLinkedInPosts(orgIds[0]);
        }
      }

      closeUploadModal();
    } catch (err) {
      console.error('Post error', err);
      setError(err.message || 'Failed to post');
    } finally {
      setIsPosting(false);
    }
  };

  const handleSchedulePost = async () => {
    if (!tokens?.idToken) { setError('Please log in'); return; }
    const userid = getRequestUserId();
    if (!userid) { setError('User ID not found. Please log in again.'); return; }
    const orgIds = Object.keys(selectedOrgs).filter(k => selectedOrgs[k]);
    if (orgIds.length === 0) { setError('Select at least one organization'); return; }

    const imageUrl = modalImage?.s3_url || modalImage?.s3_key || modalImage?.url || modalImage?.publicUrl;
    const selectedOrg = platformOrgs.find(o => o.organization_id === orgIds[0]);

    setIsScheduling(true);
    setError('');

    try {
      // In separate mode: only schedule the current item (no image_urls bundle)
      // In group mode: bundle all selected images into image_urls
      const basePayload = {
        caption: modalDesc,
        image_url: imageUrl,
        platform: modalPlatform,
        organization_id: orgIds[0],
        organization_name: selectedOrg?.organization_name || '',
        ai_suggestions: aiSuggestions || {},
        gallery_item_id: modalImage?.id || null,
        recipient_email: userInfo?.email || null,
        image_urls: (() => {
          if (isSeparateMode) return null; // each item is its own post
          const selectedItems = getSelectedItems();
          if (selectedItems.length > 1) {
            return selectedItems.map(item => item.s3_url || item.url || item.publicUrl).filter(Boolean);
          }
          return null;
        })()
      };

      let endpoint;
      let payload;

      if (postMode === 'ai') {
        endpoint = API_ENDPOINTS.SCHEDULED_POSTS_AI;
        payload = basePayload;
      } else {
        if (!scheduleDate || !scheduleTime) {
          setError('Please select a date and time for scheduling');
          setIsScheduling(false);
          return;
        }
        const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}`);
        if (isNaN(scheduledAt.getTime()) || scheduledAt <= new Date()) {
          setError('Scheduled date/time must be in the future');
          setIsScheduling(false);
          return;
        }
        endpoint = API_ENDPOINTS.SCHEDULED_POSTS;
        payload = { ...basePayload, scheduled_at: scheduledAt.toISOString() };
      }

      const res = await fetch(buildBffUrl(endpoint), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.idToken}`,
          'Content-Type': 'application/json',
          'userid': userid
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Scheduling failed');

      const scheduledTime = data.data?.scheduled_at
        ? new Date(data.data.scheduled_at).toLocaleString()
        : 'AI-selected time';

      if (isSeparateMode) {
        // Advance to the next item in the queue
        advanceToNextSeparateItem({ success: true, item: modalImage, scheduledTime });
      } else {
        setStatusMessage(
          postMode === 'ai'
            ? `Post scheduled by AI for ${scheduledTime}. It will appear in your Calendar.`
            : `Post scheduled for ${scheduledTime}. It will appear in your Calendar.`
        );
        closeUploadModal();
        clearMultiSelect();
      }
    } catch (err) {
      console.error('Schedule error', err);
      if (isSeparateMode) {
        setError(`Failed to schedule this image: ${err.message}. You can skip or retry.`);
      } else {
        setError(err.message || 'Failed to schedule post');
      }
    } finally {
      setIsScheduling(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Prompt is required for generation');
      return;
    }
    if (!tokens?.idToken) {
      setError('Please log in first');
      return;
    }
    const userid = getRequestUserId();
    setError('');
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('prompt', prompt.trim());
      formData.append('products', products);
      formData.append('marketingGoal', marketingGoal);
      formData.append('count', count);
      referenceFiles.forEach((file) => formData.append('referenceImages', file));

      const response = await fetch(buildBffUrl(API_ENDPOINTS.GALLERY_GENERATE), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${tokens.idToken}`, 'userid': userid || '' },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Generation failed');
      }

      const newItems = data.data?.items || [];
      setGalleryItems((prev) => [...newItems, ...prev]);
      setReferenceFiles([]);
    } catch (err) {
      console.error('Generate error', err);
      setError(err.message || 'Failed to generate');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadOnly = async () => {
    if (referenceFiles.length === 0) {
      setError('Add reference images to upload');
      return;
    }
    if (!tokens?.idToken) {
      setError('Please log in first');
      return;
    }
    const userid = getRequestUserId();
    setError('');
    setIsLoading(true);
    try {
      const formData = new FormData();
      referenceFiles.forEach((file) => formData.append('referenceImages', file));
      if (prompt) formData.append('prompt', prompt.trim());
      if (products) formData.append('products', products);
      if (marketingGoal) formData.append('marketingGoal', marketingGoal);

      const response = await fetch(buildBffUrl(API_ENDPOINTS.GALLERY_UPLOAD), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${tokens.idToken}`, 'userid': userid || '' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Upload failed');
      }
      const newItems = data.data?.items || [];
      setGalleryItems((prev) => [...newItems, ...prev]);
      setReferenceFiles([]);
    } catch (err) {
      console.error('Upload error', err);
      setError(err.message || 'Failed to upload');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteItem = async (item) => {
    if (!tokens?.idToken) { setError('Please log in'); return; }
    const userid = getRequestUserId();
    if (!userid) { setError('User ID not found. Please log in again.'); return; }
    if (!item?.id) { setError('This item cannot be deleted yet. Please refresh and try again.'); return; }
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Delete this poster? This will also remove it from S3.')) return;

    setDeletingItemId(item.id);
    setError('');
    try {
      const res = await fetch(buildBffUrl(`${API_ENDPOINTS.GALLERY_DELETE}/${item.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokens.idToken}`,
          'userid': userid
        }
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Delete failed');

      setGalleryItems(prev => prev.filter((g) => g.id !== item.id));
      setSelectedGalleryItems(prev => {
        const key = item.s3_key || item.s3_url;
        if (!key || !(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setStatusMessage('Poster deleted.');
    } catch (err) {
      console.error('Delete error', err);
      setError(err.message || 'Failed to delete poster');
    } finally {
      setDeletingItemId(null);
    }
  };

  return (
    <>
      <Navbar />
      <div className="gallery-shell">
        <div className="gallery-hero">
          <div>
            <p className="eyebrow">Visual workspace</p>
            <h1>Generate marketing visuals</h1>
            <p className="muted">Upload product shots, set a goal, and let Adflyn craft posters. All assets land here automatically.</p>
          </div>
          <div className="hero-badges">
            <span>Stored in S3</span>
            <span>5 files max</span>
            <span>Budget-safe prompts</span>
          </div>
        </div>

        <div className="panel">
          <div className="form-grid">
            <div className="field">
              <label>Prompt</label>
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe the poster you need" rows={3} />
            </div>
            <div className="field">
              <label>Products (comma separated)</label>
              <input value={products} onChange={(e) => setProducts(e.target.value)} placeholder="Sneakers, backpacks, hoodies" />
            </div>
            <div className="field">
              <label>Marketing goal</label>
              <input value={marketingGoal} onChange={(e) => setMarketingGoal(e.target.value)} />
            </div>
            <div className="field-inline">
              <div>
                <label>Count</label>
                <input type="number" min="1" max="5" value={count} onChange={(e) => setCount(Number(e.target.value))} />
              </div>
              <div>
                <label>Reference images</label>
                <input type="file" accept="image/*" multiple onChange={handleFileChange} />
                {referenceFiles.length > 0 && (
                  <p className="muted">{referenceFiles.length} file(s) selected</p>
                )}
              </div>
            </div>
            {error && <div className="error-banner">{error}</div>}
            {statusMessage && <div className="success-banner">{statusMessage}</div>}
            <div className="actions">
              <button className={`primary${isLoading ? ' is-loading' : ''}`} onClick={handleGenerate} disabled={isLoading} aria-busy={isLoading}>
                {isLoading ? (
                  <>
                    <span className="button-spinner" aria-hidden="true" />
                    <span>Generating…</span>
                  </>
                ) : (
                  'Generate & save'
                )}
              </button>
              <button className="ghost" onClick={handleUploadOnly} disabled={isLoading}>Upload references only</button>
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <h3>Gallery</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span className="muted">{galleryItems.length} item(s)</span>
              <button
                className={multiSelectMode ? 'primary compact-button' : 'ghost compact-button'}
                onClick={() => {
                  if (multiSelectMode) {
                    clearMultiSelect();
                  } else {
                    setMultiSelectMode(true);
                  }
                }}
              >
                {multiSelectMode ? 'Cancel' : '☑ Select Multiple'}
              </button>

              {multiSelectMode && getSelectedItems().length > 0 && (
                <>
                  {/* Schedule each image individually, step by step */}
                  <button
                    className="ghost compact-button"
                    onClick={openSeparateScheduleModal}
                    title="Schedule each image at its own time"
                  >
                    📅 Schedule Separately ({getSelectedItems().length})
                  </button>

                  {/* Bundle all images into one scheduled post */}
                  <button
                    className="primary compact-button"
                    onClick={openGroupScheduleModal}
                    title="Schedule all images as a single grouped post"
                  >
                    🗂 Schedule as Group ({getSelectedItems().length})
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="gallery-grid">
            {galleryItems.map((item) => {
              const itemKey = item.s3_key || item.s3_url;
              const isSelected = !!selectedGalleryItems[itemKey];
              return (
                <div
                  key={itemKey}
                  className={`gallery-card${multiSelectMode ? ' selectable' : ''}${isSelected ? ' selected' : ''}`}
                  onClick={multiSelectMode ? () => toggleGalleryItemSelect(itemKey) : undefined}
                >
                  {multiSelectMode && (
                    <div className="gallery-select-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleGalleryItemSelect(itemKey)}
                        onClick={e => e.stopPropagation()}
                        aria-label="Select image"
                      />
                    </div>
                  )}
                  {item.s3_url && (
                    <img src={item.s3_url} alt={item.prompt || 'Generated'} />
                  )}
                  <div className="meta">
                    <p className="title">{item.prompt || 'Uploaded asset'}</p>
                    {item.products && <p className="muted small">Products: {Array.isArray(item.products) ? item.products.join(', ') : item.products}</p>}
                    {item.marketing_goal && <p className="muted small">Goal: {item.marketing_goal}</p>}
                    <span className="tag">{item.source || 'generation'}</span>
                    {!multiSelectMode && (
                      <div className="card-actions">
                        <button className="ghost" onClick={() => openUploadModal(item)}>Upload / Share</button>
                        <button
                          className="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteItem(item);
                          }}
                          disabled={deletingItemId === item.id || !item.id}
                        >
                          {deletingItemId === item.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {galleryItems.length === 0 && (
              <div className="empty">No assets yet. Generate your first batch above.</div>
            )}
          </div>
        </div>
        {showUploadModal && (
          <div className="modal-backdrop">
            <div className="modal">
              <div className="modal-header">
                <div>
                  <p className="eyebrow">
                    {isSeparateMode
                      ? `Scheduling separately — ${separateScheduleIndex + 1} of ${separateScheduleQueue.length}`
                      : getSelectedItems().length > 1
                        ? `Group post — ${getSelectedItems().length} images`
                        : 'Publish'}
                  </p>
                  <h3>
                    {isSeparateMode
                      ? (modalImage?.prompt || `Image ${separateScheduleIndex + 1}`)
                      : 'Share Image'}
                  </h3>
                  {/* Step progress dots for separate mode */}
                  {isSeparateMode && separateScheduleQueue.length > 1 && (
                    <div className="step-progress">
                      {separateScheduleQueue.map((_, i) => (
                        <span
                          key={i}
                          className={`step-dot${i < separateScheduleIndex ? ' done' : i === separateScheduleIndex ? ' active' : ''}`}
                          aria-label={i < separateScheduleIndex ? 'Done' : i === separateScheduleIndex ? 'Current' : 'Pending'}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {/* Skip button in separate mode */}
                  {isSeparateMode && separateScheduleQueue.length > 1 && (
                    <button
                      className="ghost compact-button"
                      onClick={() => advanceToNextSeparateItem(null)}
                      disabled={isScheduling}
                      title="Skip this image"
                    >
                      Skip →
                    </button>
                  )}
                  <button className="ghost modal-close" onClick={closeUploadModal} disabled={isPosting || isScheduling}>×</button>
                </div>
              </div>
              <div className="modal-body">
                <div className="modal-preview">
                  {modalImage?.s3_url ? (
                    <img src={modalImage.s3_url} alt={modalImage.prompt || 'Preview'} />
                  ) : (
                    <div className="preview-empty">No preview available</div>
                  )}
                  <div className="preview-copy">
                    <p className="title">{modalImage?.prompt || 'Untitled asset'}</p>
                    {isSeparateMode ? (
                      <p className="muted small">
                        Scheduling image <strong>{separateScheduleIndex + 1}</strong> of <strong>{separateScheduleQueue.length}</strong> individually.
                      </p>
                    ) : getSelectedItems().length > 1 ? (
                      <p className="muted small">
                        <strong>{getSelectedItems().length} images</strong> will be bundled into one scheduled post.
                      </p>
                    ) : (
                      <p className="muted small">Choose a platform and one or more orgs, then publish.</p>
                    )}
                  </div>
                  {/* Group mode: multi-image thumbnail strip */}
                  {!isSeparateMode && getSelectedItems().length > 1 && (
                    <div className="multi-image-strip">
                      {getSelectedItems().map((item, idx) => (
                        <div key={item.s3_key || item.s3_url || idx} className="multi-image-thumb">
                          {item.s3_url && <img src={item.s3_url} alt={`Image ${idx + 1}`} />}
                          <span className="thumb-index">{idx + 1}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Separate mode: show remaining queue thumbnails */}
                  {isSeparateMode && separateScheduleQueue.length > 1 && (
                    <div className="multi-image-strip">
                      {separateScheduleQueue.map((item, idx) => (
                        <div
                          key={item.s3_key || item.s3_url || idx}
                          className={`multi-image-thumb${idx === separateScheduleIndex ? ' thumb-active' : idx < separateScheduleIndex ? ' thumb-done' : ''}`}
                        >
                          {item.s3_url && <img src={item.s3_url} alt={`Image ${idx + 1}`} />}
                          <span className="thumb-index">
                            {idx < separateScheduleIndex ? '✓' : idx + 1}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="preview-grid">
                  <div className="suggestion-panel">
                    <div className="panel-head compact-head">
                      <h4>AI Caption Suggestion</h4>
                      <span className="muted small">LinkedIn-ready copy</span>
                    </div>
                    {suggestionsLoading && <div className="muted">Fetching caption suggestions...</div>}
                    {!suggestionsLoading && suggestionsError && <div className="error-banner compact">{suggestionsError}</div>}
                    {!suggestionsLoading && !suggestionsError && (
                      <>
                        <p className="suggestion-text">{getLinkedInCaptionSuggestion(aiSuggestions) || 'The AI caption will appear here after analysis.'}</p>
                        {aiSuggestions?.platforms?.find((platform) => (platform?.platform_name || '').toLowerCase() === 'linkedin')?.why && (
                          <p className="muted small">{aiSuggestions.platforms.find((platform) => (platform?.platform_name || '').toLowerCase() === 'linkedin').why}</p>
                        )}
                        <button
                          type="button"
                          className="ghost compact-button"
                          onClick={() => {
                            const suggestedCaption = getLinkedInCaptionSuggestion(aiSuggestions);
                            if (suggestedCaption) {
                              setModalDesc(suggestedCaption);
                            }
                          }}
                        >
                          Use suggested caption
                        </button>
                      </>
                    )}
                  </div>

                  <div className="linkedin-preview-panel">
                    <div className="preview-platform-tag">LinkedIn Feed Preview</div>
                    <div className="linkedin-preview-card">
                      <div className="linkedin-preview-top">
                        <div className="preview-avatar">{(userInfo?.name || userInfo?.email || 'A').charAt(0).toUpperCase()}</div>
                        <div className="preview-identity">
                          <p className="preview-name">{userInfo?.name || 'Adflyn User'}</p>
                          <p className="preview-headline">Marketing at Adflyn</p>
                          <p className="preview-post-meta">1h • Edited • Public</p>
                        </div>
                        <button type="button" className="preview-follow-btn">+ Follow</button>
                      </div>

                      <p className="preview-caption">{modalDesc || getLinkedInCaptionSuggestion(aiSuggestions) || 'Your LinkedIn caption will appear here.'}</p>

                      <div className="linkedin-preview-body">
                        {modalImage?.s3_url ? (
                          <img src={modalImage.s3_url} alt={modalImage.prompt || 'LinkedIn preview'} />
                        ) : (
                          <div className="preview-empty preview-empty-wide">No preview available</div>
                        )}
                      </div>

                      <div className="preview-engagement-row">
                        <div className="preview-reactions">
                          <span>👍</span>
                          <span>👏</span>
                          <span>❤️</span>
                          <small>128</small>
                        </div>
                        <div className="preview-engagement-meta">14 comments • 6 reposts</div>
                      </div>

                      <div className="preview-actions-row">
                        <span>👍 Like</span>
                        <span>💬 Comment</span>
                        <span>🔁 Repost</span>
                        <span>✉️ Send</span>
                      </div>
                    </div>
                  </div>
                </div>

                {userInfo?.email && (
                  <p className="muted small email-note">A confirmation email will be sent to {userInfo.email} after the post goes live.</p>
                )}

                <div className="field">
                  <label>Description</label>
                  <textarea value={modalDesc} onChange={(e) => setModalDesc(e.target.value)} rows={4} placeholder="Write the caption for this post" />
                </div>

                <div className="field">
                  <label>Platform</label>
                  <div className="platform-select">
                    <button type="button" className={modalPlatform === 'linkedin' ? 'active' : ''} onClick={() => togglePlatform('linkedin')}>LinkedIn</button>
                    <button type="button" className={modalPlatform === 'facebook' ? 'active' : ''} onClick={() => togglePlatform('facebook')}>Facebook</button>
                    <button type="button" className={modalPlatform === 'instagram' ? 'active' : ''} onClick={() => togglePlatform('instagram')}>Instagram</button>
                  </div>
                </div>

                <div className="orgs-panel">
                  <div className="orgs-header">
                    <div>
                      <label>Organizations</label>
                      <p className="muted small">Connected accounts available for {modalPlatform}</p>
                    </div>
                    <label className="select-all">
                      <input type="checkbox" checked={selectAllOrgs} onChange={toggleSelectAll} />
                      Select all
                    </label>
                  </div>
                  <div className="orgs-list">
                    {orgsLoading && <div className="muted">Loading organizations...</div>}
                    {!orgsLoading && orgsError && <div className="error-banner compact">{orgsError}</div>}
                    {!orgsLoading && platformOrgs.length === 0 && !orgsError && <div className="muted">No organizations found for {modalPlatform}.</div>}
                    {platformOrgs.map((o) => {
                      const id = o.organization_id || o.id || o.id_str || o.idString;
                      return (
                        <div key={id} className="org-row">
                          <label className="org-check">
                            <input type="checkbox" checked={!!selectedOrgs[id]} onChange={() => toggleOrgCheckbox(id)} />
                            <span>
                              <strong>{o.organization_name || o.name || o.localizedName || o.displayName || o.handle || id}</strong>
                              <small>{id}</small>
                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* ── Scheduler ── */}
              <div className="scheduler-panel">
                <div className="panel-head compact-head">
                  <h4>When to post</h4>
                </div>
                <div className="post-mode-tabs">
                  <button
                    type="button"
                    className={postMode === 'now' ? 'active' : ''}
                    onClick={() => setPostMode('now')}
                  >
                    Post now
                  </button>
                  <button
                    type="button"
                    className={postMode === 'schedule' ? 'active' : ''}
                    onClick={() => setPostMode('schedule')}
                  >
                    Schedule
                  </button>
                  <button
                    type="button"
                    className={postMode === 'ai' ? 'active' : ''}
                    onClick={() => setPostMode('ai')}
                  >
                    ✨ AI schedule
                  </button>
                </div>

                {postMode === 'schedule' && (
                  <div className="schedule-inputs">
                    <div className="field">
                      <label>Date</label>
                      <input
                        type="date"
                        value={scheduleDate}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={(e) => setScheduleDate(e.target.value)}
                      />
                    </div>
                    <div className="field">
                      <label>Time</label>
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {postMode === 'ai' && (
                  <p className="muted small ai-schedule-note">
                    ✨ AI will pick the best time — typically the next weekday at 10 AM. The post will appear in your Calendar.
                  </p>
                )}
              </div>

              <div className="modal-actions">
                <button className="ghost" onClick={closeUploadModal} disabled={isPosting || isScheduling}>Cancel</button>
                {postMode === 'now' ? (
                  <button
                    className="primary post-button"
                    onClick={handlePostToPlatforms}
                    disabled={isLoading || isPosting}
                    aria-busy={isPosting}
                  >
                    {isPosting ? (
                      <>
                        <span className="button-spinner" aria-hidden="true" />
                        <span>Posting…</span>
                      </>
                    ) : (
                      'Post now'
                    )}
                  </button>
                ) : (
                  <button
                    className="primary post-button"
                    onClick={handleSchedulePost}
                    disabled={isScheduling}
                    aria-busy={isScheduling}
                  >
                    {isScheduling ? (
                      <>
                        <span className="button-spinner" aria-hidden="true" />
                        <span>Scheduling…</span>
                      </>
                    ) : postMode === 'ai' ? (
                      '✨ Schedule with AI'
                    ) : (
                      'Schedule post'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Gallery;
